import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Box,
  Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { InvoiceItem } from '../../types/invoice';

interface InvoiceItemsProps {
  items: InvoiceItem[];
  isEditing: boolean;
  onItemsChange: (items: InvoiceItem[]) => void;
}

const InvoiceItems: React.FC<InvoiceItemsProps> = ({ items = [], isEditing, onItemsChange }) => {
  const [localItems, setLocalItems] = useState<InvoiceItem[]>([]);

  // Initialize local items only when items prop changes
  useEffect(() => {
    setLocalItems([...items]);
  }, [items]);

  // Helper function to safely format numbers
  const formatNumber = (value: any): string => {
    const num = Number(value);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  const calculateItemTotals = (item: Partial<InvoiceItem> & { id: number; invoiceId: number; createdAt: string; updatedAt: string }): InvoiceItem => {
    const quantity = Number(item.quantity) || 0;
    const unitPrice = Number(item.unitPrice) || 0;
    const vatRate = Number(item.vatRate) || 21;
    
    const netAmount = quantity * unitPrice;
    const vatAmount = netAmount * (vatRate / 100);
    const totalPrice = netAmount + vatAmount;

    return {
      id: item.id,
      invoiceId: item.invoiceId,
      description: item.description || '',
      quantity,
      unitPrice,
      vatRate,
      netAmount: Math.round(netAmount * 100) / 100,
      vatAmount: Math.round(vatAmount * 100) / 100,
      totalPrice: Math.round(totalPrice * 100) / 100,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt
    };
  };

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: any) => {
    if (!isEditing) return;

    const updatedItems = [...localItems];
    const currentItem = updatedItems[index];
    const updatedItem = {
      ...currentItem,
      [field]: value
    };

    // Recalculate totals for this item
    updatedItems[index] = calculateItemTotals({
      ...updatedItem,
      id: currentItem.id,
      invoiceId: currentItem.invoiceId,
      createdAt: currentItem.createdAt,
      updatedAt: currentItem.updatedAt
    });

    setLocalItems(updatedItems);
    onItemsChange(updatedItems);
  };

  const addItem = () => {
    if (!isEditing) return;

    const newItem = calculateItemTotals({
      id: Date.now(), // Temporary ID for new items
      invoiceId: 0, // Will be set by backend
      description: '',
      quantity: 1,
      unitPrice: 0,
      vatRate: 21,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    const updatedItems = [...localItems, newItem];
    setLocalItems(updatedItems);
    onItemsChange(updatedItems);
  };

  const removeItem = (index: number) => {
    if (!isEditing) return;

    const updatedItems = localItems.filter((_, i) => i !== index);
    setLocalItems(updatedItems);
    onItemsChange(updatedItems);
  };

  const displayItems = isEditing ? localItems : items;

  if (displayItems.length === 0) {
    return (
      <Box sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">
          {isEditing
            ? 'Žádné položky. Klikněte na "Přidat položku" pro vytvoření první položky.'
            : 'Tato faktura nemá žádné položky.'
          }
        </Typography>
        {isEditing && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addItem}
            sx={{ mt: 2 }}
          >
            Přidat položku
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '300px' }}>
      <TableContainer 
        component={Paper} 
        variant="outlined"
        sx={{ 
          maxHeight: '60vh',
          overflow: 'auto',
          position: 'relative'
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Popis</TableCell>
              <TableCell align="right" sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Množství</TableCell>
              <TableCell align="right" sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Jednotková cena</TableCell>
              <TableCell align="right" sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Čistá částka</TableCell>
              <TableCell align="right" sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>DPH %</TableCell>
              <TableCell align="right" sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>DPH</TableCell>
              <TableCell align="right" sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Celkem</TableCell>
              {isEditing && <TableCell align="center" sx={{ backgroundColor: 'background.paper', fontWeight: 'bold' }}>Akce</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayItems.map((item, index) => (
              <TableRow 
                key={item.id || `new-item-${index}`}
                sx={{ 
                  height: '60px', // Fixed row height
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <TableCell sx={{ verticalAlign: 'middle' }}>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      value={item.description}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                  ) : (
                    item.description
                  )}
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                  {isEditing ? (
                    <TextField
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={{ width: 80 }}
                    />
                  ) : (
                    item.quantity
                  )}
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                  {isEditing ? (
                    <TextField
                      type="number"
                      value={item.unitPrice}
                      onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={{ width: 100 }}
                    />
                  ) : (
                    `${formatNumber(item.unitPrice)} CZK`
                  )}
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                  {formatNumber(item.netAmount)} CZK
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                  {isEditing ? (
                    <TextField
                      type="number"
                      value={item.vatRate}
                      onChange={(e) => handleItemChange(index, 'vatRate', parseFloat(e.target.value) || 0)}
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      sx={{ width: 80 }}
                    />
                  ) : (
                    `${item.vatRate}%`
                  )}
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                  {formatNumber(item.vatAmount)} CZK
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                  <strong>{formatNumber(item.totalPrice)} CZK</strong>
                </TableCell>
                {isEditing && (
                  <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                    <IconButton
                      onClick={() => removeItem(index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {isEditing && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addItem}
          >
            Přidat položku
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default InvoiceItems;
